import { baselineTemplates, type Template } from '@/features/policies/lib/regoTemplates'
import type { PolicyArtefactType, PolicyItem } from '@/lib/apiTypes'

/** The artefacts a baseline covers, in the order the dialog lays them out. `plan` is absent:
 *  its verdicts do not gate yet, so there is nothing to recommend. */
export const BASELINE_ARTEFACTS: readonly PolicyArtefactType[] = ['module', 'state', 'provider']

/** One card in the dialog. */
export interface BaselineRow {
  artefactType: PolicyArtefactType
  template: Template
  /** The org already has a policy by this name for this artefact, so applying would do nothing.
   *  The row is shown, marked, and not selectable - never silently dropped, because "you already
   *  have this" is the useful half of the answer. */
  exists: boolean
  /** Stable identity for selection state and React keys. Unique across artefacts, which matters
   *  because the same label can exist under two of them. */
  key: string
}

/** What one row becomes on the wire. Mirrors an entry of POST .../policies/baseline. */
export interface BaselinePolicyInput {
  artefact_type: PolicyArtefactType
  name: string
  description: string
  rego: string
}

/** Artefact + trimmed, case-folded name.
 *
 *  The fold is the whole trick. Template labels are sentence case ("No secrets in state") while
 *  the names orgs are carrying are not ("No Secrets in State" - what the old org-creation seed
 *  wrote), and without folding, every one of those orgs would be offered a second copy of a
 *  policy it already has. The server folds the same way; this one only exists so the dialog can
 *  mark the card before anyone presses Apply. */
export function baselineKey(artefactType: PolicyArtefactType, name: string): string {
  return `${artefactType}\u0000${name.trim().toLowerCase()}`
}

/**
 * Build the dialog's rows: every baseline template for each artefact, each marked with whether
 * the org already has a policy of that name.
 *
 * Only ORG-BASELINE policies count as a match (`instance_key === null`). An identity-custom
 * policy is attached to one module or provider and says nothing about whether the org has the
 * org-wide rule, so letting one block the baseline would hide a gate the org does not have.
 */
export function buildBaselineRows(
  existing: readonly PolicyItem[],
  artefacts: readonly PolicyArtefactType[] = BASELINE_ARTEFACTS,
): BaselineRow[] {
  const taken = new Set(
    existing
      .filter((p) => p.instance_key === null || p.instance_key === undefined)
      .map((p) => baselineKey(p.artefact_type, p.name)),
  )
  return artefacts.flatMap((artefactType) =>
    baselineTemplates(artefactType).map((template) => {
      const key = baselineKey(artefactType, template.label)
      return { artefactType, template, key, exists: taken.has(key) }
    }),
  )
}

/** The rows a fresh dialog starts with ticked: everything the org does not already have. */
export function defaultSelection(rows: readonly BaselineRow[]): Set<string> {
  return new Set(rows.filter((r) => !r.exists).map((r) => r.key))
}

/** The request payload for the ticked rows. An existing row can never contribute, whatever the
 *  selection set says - the checkbox is disabled, so a key for one is stale state, not intent. */
export function toRequestPolicies(rows: readonly BaselineRow[], selected: ReadonlySet<string>): BaselinePolicyInput[] {
  return rows
    .filter((r) => !r.exists && selected.has(r.key))
    .map((r) => ({
      artefact_type: r.artefactType,
      name: r.template.label,
      description: r.template.description,
      rego: r.template.rego,
    }))
}

/** Per-column counts for the "n of m selected" line. `m` counts only what CAN be selected, so a
 *  column whose every card already exists reads "0 of 0" rather than claiming unticked work. */
export function columnCounts(rows: readonly BaselineRow[], selected: ReadonlySet<string>): {
  selectable: number; selected: number; exists: number
} {
  const selectable = rows.filter((r) => !r.exists)
  return {
    selectable: selectable.length,
    selected: selectable.filter((r) => selected.has(r.key)).length,
    exists: rows.length - selectable.length,
  }
}

/**
 * The sentence the toast says after an apply.
 *
 * Both halves are worth saying out loud. `created` is the answer to "did that work", and
 * `skipped` is the answer to "why fewer than I ticked" - a name that got taken between opening
 * the dialog and pressing Apply is reported by the server, not by the dialog, so this is the only
 * place the user would hear about it.
 */
export function applySummary(created: number, skipped: number): string {
  const policies = (n: number) => `${n} polic${n === 1 ? 'y' : 'ies'}`
  if (created === 0 && skipped > 0) return `Nothing to add - ${policies(skipped)} already existed.`
  if (created === 0) return 'No policies added.'
  if (skipped > 0) return `Added ${policies(created)}. ${policies(skipped)} already existed and were left alone.`
  return `Added ${policies(created)}.`
}
